<template>
  <div class="modal-overlay" @click.self="$emit('close')">
    <div class="modal-content recovery-modal">
      <!-- Modal Header -->
      <div class="modal-header-premium">
        <div class="header-icon" :class="{ 'primary': confirmRegenerate }">
          <i class="fas" :class="confirmRegenerate ? 'fa-sync-alt' : 'fa-key'"></i>
        </div>
        <div class="header-text">
          <h4>{{ confirmRegenerate ? 'Regenerate Codes' : 'Recovery Codes' }}</h4>
          <p>{{ confirmRegenerate ? 'Invalidate current codes and create new ones' : 'Secondary access to your account' }}</p>
        </div>
        <button class="close-btn" @click="$emit('close')">&times;</button>
      </div>

      <div class="modal-body-premium">
        <!-- Main View -->
        <div v-if="!confirmRegenerate">
          <div v-if="loading" class="loading-state">
            <div class="spinner-border text-primary"></div>
          </div>

          <!-- Codes List (Only visible after regeneration) -->
          <div v-else-if="codes.length > 0">
            <p class="description-text">
              <strong>Success!</strong> Here are your new recovery codes. 
              <span class="text-danger d-block mt-1">Please save them now. You won't be able to see them again.</span>
            </p>

            <div class="codes-container">
              <div class="codes-grid">
                <div 
                  v-for="item in codes" 
                  :key="item.id || item" 
                  class="code-item"
                >
                  <span class="code-text">{{ item.code || item }}</span>
                </div>
              </div>
            </div>

            <div class="action-bar mt-4 justify-content-center">
              <button class="btn-secondary-clean w-100" @click="downloadCodes">
                <i class="fas fa-download me-2"></i> Download Codes
              </button>
            </div>
          </div>

          <!-- Summary View (Default security state) -->
          <div v-else class="text-center py-3">
             <div class="mb-4">
                <div class="security-shield">
                    <i class="fas fa-shield-alt"></i>
                </div>
             </div>
             
             <h5 class="mb-2 font-weight-bold">{{ unusedCount }} Unused Codes Remaining</h5>
             <p class="text-muted mb-4 small px-4">
                 For security, we don't display existing recovery codes. You can generate a new set if you've lost yours.
             </p>

             <button class="btn-primary-premium w-100" @click="confirmRegenerate = true">
                <i class="fas fa-sync-alt me-2"></i> Generate New Codes
             </button>
          </div>
        </div>

        <!-- Regenerate Confirmation View -->
        <div v-else class="regenerate-flow">
          <div class="form-group-clean">
            <label>Confirm Your Password</label>
            <input 
              v-model="password" 
              type="password" 
              class="input-clean" 
              placeholder="Enter current password"
              @keyup.enter="handleRegenerate"
            />
          </div>

          <div v-if="error" class="error-text mt-2">{{ error }}</div>
        </div>
      </div>

      <!-- Modal Footer -->
      <div class="modal-footer-premium">
        <template v-if="!confirmRegenerate">
          <button class="btn-secondary-clean" @click="$emit('close')">Close</button>
        </template>
        <template v-else>
          <button class="btn-secondary-clean" @click="confirmRegenerate = false">Cancel</button>
          <button 
            class="btn-confirm-danger" 
            @click="handleRegenerate" 
            :disabled="!password || regenerating"
          >
            <i v-if="regenerating" class="fas fa-spinner fa-spin me-2"></i>
            {{ regenerating ? 'Generating...' : 'Confirm Generate' }}
          </button>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';

/**
 * Component to display and manage 2FA recovery codes.
 * Securely handles code generation and display.
 */
export default {
    name: 'RecoveryCodes',
    data() {
        return {
            codes: [],
            unusedCount: 0,
            loading: false,
            confirmRegenerate: true,
            password: '',
            regenerating: false,
            error: ''
        };
    },
    mounted() {
        this.fetchCodes();
    },
    methods: {
        ...mapActions('TwoFactor', ['getRecoveryCodes', 'regenerateRecoveryCodes']),
        
        async fetchCodes() {
            this.loading = true;
            try {
                const res = await this.getRecoveryCodes();
                const data = res.data || res.message || res;
                
                // If API returns summary count
                if (data.unused_codes_count !== undefined) {
                    this.unusedCount = data.unused_codes_count;
                    this.codes = []; // Hide codes
                } 
                // Fallback if API sends codes array (unlikely per new requirement)
                else if (Array.isArray(data) || data.recovery_codes) {
                    const list = Array.isArray(data) ? data : data.recovery_codes;
                    this.unusedCount = list.length;
                    this.codes = []; // Still hide them initially for consistency
                }
            } catch (err) {
                console.error('[RecoveryCodes] Load error:', err);
                this.error = 'Failed to load status.';
            } finally {
                this.loading = false;
            }
        },
        
        async handleRegenerate() {
            if (!this.password) return;
            
            this.regenerating = true;
            this.error = '';
            
            try {
                const res = await this.regenerateRecoveryCodes(this.password);
                const data = res.data || res.message || res;
                const newCodes = Array.isArray(data) ? data : (data.recovery_codes || []);
                
                this.codes = newCodes;
                this.unusedCount = newCodes.length;
                this.confirmRegenerate = false;
                this.password = '';
            } catch (err) {
                this.error = err.response?.data?.message || 'Failed to regenerate. Check password.';
            } finally {
                this.regenerating = false;
            }
        },
        
        downloadCodes() {
            const header = "--- RECOVERY CODES ---\n";
            const info = "Note: Each recovery code can only be used once.\n";
            const separator = "----------------------\n\n";
            const codeList = this.codes.map(c => c.code || c).join('\n');
            
            const content = header + info + separator + codeList;
            const blob = new Blob([content], { type: 'text/plain' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `recovery-codes-${new Date().getTime()}.txt`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        }
    }
}
</script>

<style scoped>
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(4px);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1060;
}

.recovery-modal {
    background: white;
    width: 90%;
    max-width: 480px;
    border-radius: 20px;
    overflow: hidden;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}

.modal-header-premium {
    padding: 24px 32px;
    background: #ffffff;
    border-bottom: 1px solid #f3f4f6;
    display: flex;
    align-items: center;
    gap: 16px;
    position: relative;
}

.header-icon {
    width: 42px;
    height: 42px;
    background: var(--secondary-color, #fc6666);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--primary-color, #fc6666);
    font-size: 18px;
}

.header-icon.primary {
    background: var(--secondary-color, #fc6666);
    color: var(--primary-color, #fc6666);
}

.header-text h4 {
    margin: 0;
    font-size: 18px;
    font-weight: 700;
    color: #111827;
}

.header-text p {
    margin: 2px 0 0;
    font-size: 13px;
    color: #6b7280;
}

.close-btn {
    position: absolute;
    top: 24px;
    right: 24px;
    background: none;
    border: none;
    font-size: 24px;
    color: #9ca3af;
    cursor: pointer;
}

.modal-body-premium {
    padding: 32px;
}

.security-shield {
    width: 80px;
    height: 80px;
    background: var(--secondary-color, #eff6ff);
    color: var(--primary-color, #3b82f6);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 32px;
    margin: 0 auto;
}

.codes-container {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 16px;
    margin-top: 16px;
}

.codes-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
}

.code-item {
    background: white;
    padding: 8px;
    border-radius: 6px;
    text-align: center;
    font-family: 'JetBrains Mono', monospace;
    font-weight: 600;
    font-size: 14px;
    color: #1e293b;
    border: 1px solid #f1f5f9;
}

.alert-danger-clean {
    background: #fef2f2;
    border: 1px solid #fecaca;
    padding: 16px;
    border-radius: 12px;
    display: flex;
    gap: 12px;
    color: #991b1b;
    font-size: 13px;
    line-height: 1.5;
}

.form-group-clean label {
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: #374151;
    margin-bottom: 8px;
}

.input-clean {
    width: 100%;
    padding: 12px;
    border: 1px solid #d1d5db;
    border-radius: 10px;
    font-size: 14px;
    outline: none;
    transition: all 0.2s;
}

.input-clean:focus {
    border-color: var(--primary-color, #fc6666);
    box-shadow: 0 0 0 3px var(--secondary-color, rgba(252, 102, 102, 0.1));
}

.modal-footer-premium {
    padding: 20px 32px;
    background: #f9fafb;
    border-top: 1px solid #f3f4f6;
    display: flex;
    justify-content: flex-end;
    gap: 12px;
}

.btn-secondary-clean {
    padding: 10px 20px;
    border: 1px solid #e5e7eb;
    background: white;
    color: #4b5563;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
}

.btn-primary-premium {
    padding: 12px 24px;
    background: var(--primary-color, #fc6666);
    color: white;
    border: none;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    box-shadow: 0 4px 6px -1px var(--secondary-color, rgba(252, 102, 102, 0.2));
}

.btn-confirm-danger {
    padding: 10px 24px;
    background: var(--primary-color, #fc6666);
    border: none;
    color: white;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
}

.btn-confirm-danger:disabled {
    opacity: 0.7;
    cursor: not-allowed;
}

.error-text {
    color: #dc2626;
    font-size: 12px;
    text-align: center;
}

.loading-state {
    display: flex;
    justify-content: center;
    padding: 40px;
}
</style>

